import { useState, useEffect, useCallback } from "react";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";

// Sub-components
import { TasksHeader } from "../components/tasks/TasksHeader";
import { TasksFilters } from "../components/tasks/TasksFilters";
import { TasksList } from "../components/tasks/TasksList";
import { TaskFormDialog } from "../components/tasks/TaskFormDialog";

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filter, setFilter] = useState("all");
  const [memberFilter, setMemberFilter] = useState("all");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    assigned_to: "",
    priority: "medium",
    due_date: null,
  });

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, membersRes] = await Promise.all([
        axios.get(`${API}/tasks`),
        axios.get(`${API}/members`),
      ]);
      setTasks(tasksRes.data);
      setMembers(membersRes.data);
    } catch (error) {
      toast.error("Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Task title is required");
      return;
    }

    try {
      const data = {
        ...formData,
        due_date: formData.due_date
          ? format(formData.due_date, "yyyy-MM-dd")
          : null,
        assigned_to: formData.assigned_to || null,
      };

      if (editingTask) {
        const res = await axios.put(`${API}/tasks/${editingTask.id}`, data);
        setTasks((prev) =>
          prev.map((t) => (t.id === editingTask.id ? res.data : t))
        );
        toast.success("Task updated");
      } else {
        const res = await axios.post(`${API}/tasks`, data);
        setTasks((prev) => [res.data, ...prev]);
        toast.success("Task created");
      }

      closeModal();
    } catch (error) {
      toast.error("Failed to save task");
    }
  };

  const toggleComplete = async (task) => {
    try {
      const res = await axios.put(`${API}/tasks/${task.id}`, {
        completed: !task.completed,
      });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? res.data : t)));
      toast.success(res.data.completed ? "Task completed!" : "Task reopened");
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const deleteTask = async (id) => {
    try {
      await axios.delete(`${API}/tasks/${id}`);
      setTasks((prev) => prev.filter((t) => t.id !== id));
      toast.success("Task deleted");
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const openEditModal = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || "",
      assigned_to: task.assigned_to || "",
      priority: task.priority,
      due_date: task.due_date ? new Date(task.due_date) : null,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingTask(null);
    setFormData({
      title: "",
      description: "",
      assigned_to: "",
      priority: "medium",
      due_date: null,
    });
  };

  const filteredTasks = tasks.filter((task) => {
    if (filter === "completed" && !task.completed) return false;
    if (filter === "pending" && task.completed) return false;
    if (memberFilter !== "all" && task.assigned_to !== memberFilter) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <TasksHeader
        pendingCount={filteredTasks.filter((t) => !t.completed).length}
        onAddTask={() => setModalOpen(true)}
      />

      <TasksFilters
        filter={filter}
        setFilter={setFilter}
        memberFilter={memberFilter}
        setMemberFilter={setMemberFilter}
        members={members}
      />

      <TasksList
        tasks={filteredTasks}
        onToggle={toggleComplete}
        onEdit={openEditModal}
        onDelete={deleteTask}
      />

      <TaskFormDialog
        open={modalOpen}
        onOpenChange={setModalOpen}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        isEditing={!!editingTask}
        members={members}
      />
    </div>
  );
}
